// <vita-mega-nav>: the header mega menus (P1-03, blocks/_vita-mega-menu.liquid).
// The panels are server rendered inside each nav item, and CSS opens them on hover and focus until
// this element is defined. This adds
// - hover intent: opens after a short pause, switches straight across while one is open, closes after a grace period;
// - click (or Enter / Space) on the trigger toggles; ArrowDown opens and moves to the first link;
// - Esc closes and returns focus to the trigger; focus or a click leaving the item closes it;
// - the theme editor opens the panel of the selected block;
// - vita:nav_click on every panel link.

const DESKTOP = window.matchMedia('(min-width: 1100px)');
const OPEN_DELAY = 120;
const CLOSE_DELAY = 450;
const CLICK_GRACE = 500;

class VitaMegaNav extends HTMLElement {
  #current = null;
  #timer = 0;
  #hoverOpenedAt = 0;

  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-vita-mega]')];
    for (const item of this.items) {
      const trigger = item.querySelector('[data-vita-mega-trigger]');
      item.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.#hoverIn(item); });
      item.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.#hoverOut(item); });
      trigger.addEventListener('click', () => this.#onTriggerClick(item));
      item.addEventListener('keydown', (e) => this.#onKeydown(e, item, trigger));
      item.addEventListener('focusout', (e) => {
        if (item.contains(e.relatedTarget) || (!e.relatedTarget && item.matches(':hover'))) return;
        if (this.#current === item) this.close();
      });
    }
    this.addEventListener('click', this.#onLinkClick);
    document.addEventListener('pointerdown', this.#onOutside);
    document.addEventListener('keydown', this.#onDocKeydown);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    DESKTOP.addEventListener('change', this.#onBreakpoint);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    document.removeEventListener('pointerdown', this.#onOutside);
    document.removeEventListener('keydown', this.#onDocKeydown);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    DESKTOP.removeEventListener('change', this.#onBreakpoint);
  }

  get isOpen() {
    return this.#current !== null;
  }

  open(item) {
    clearTimeout(this.#timer);
    if (this.#current === item) return;
    if (this.#current) this.#set(this.#current, false);
    this.#current = item;
    this.#set(item, true);
    this.toggleAttribute('data-open', true);
  }

  close() {
    clearTimeout(this.#timer);
    if (!this.#current) return;
    this.#set(this.#current, false);
    this.#current = null;
    this.removeAttribute('data-open');
  }

  #set(item, open) {
    item.toggleAttribute('data-open', open);
    item.querySelector('[data-vita-mega-trigger]')?.setAttribute('aria-expanded', String(open));
  }

  // --- pointer ---
  #hoverIn(item) {
    clearTimeout(this.#timer);
    if (this.#current === item) return;
    const openNow = () => { this.open(item); this.#hoverOpenedAt = performance.now(); };
    if (this.#current) openNow();
    else this.#timer = setTimeout(openNow, OPEN_DELAY);
  }

  #hoverOut(item) {
    clearTimeout(this.#timer);
    if (this.#current !== item) return;
    if (item.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) return;
    this.#timer = setTimeout(() => this.close(), CLOSE_DELAY);
  }

  #onTriggerClick(item) {
    // a click that lands right after hover opened the panel should not shut it again
    if (this.#current === item && performance.now() - this.#hoverOpenedAt > CLICK_GRACE) this.close();
    else this.open(item);
    this.#hoverOpenedAt = 0;
  }

  #onOutside = (e) => {
    if (this.#current && !this.#current.contains(e.target)) this.close();
  };

  // --- keyboard ---
  // Esc listens on the document: Safari doesn't focus a button on click, so after a mouse open the key
  // goes to the body, not the item. Focus returns to the trigger only when it was in the panel.
  #onDocKeydown = (e) => {
    if (e.key !== 'Escape' || !this.#current) return;
    const item = this.#current;
    e.preventDefault();
    this.close();
    if (item.contains(document.activeElement)) item.querySelector('[data-vita-mega-trigger]')?.focus();
  };

  #onKeydown(e, item, trigger) {
    if (e.key === 'ArrowDown' && e.target === trigger) {
      e.preventDefault();
      this.open(item);
      item.querySelector('[data-vita-mega-panel] a')?.focus();
    }
  }

  #onBreakpoint = () => this.close();

  // --- theme editor ---
  #onBlockSelect = (e) => {
    const item = this.items.find((i) => i.contains(e.target));
    if (item) this.open(item);
  };

  #onBlockDeselect = (e) => {
    if (this.#current?.contains(e.target)) this.close();
  };

  // --- analytics ---
  #onLinkClick = (e) => {
    const a = e.target.closest('[data-vita-mega-panel] a[href]');
    if (!a || typeof window.vitaTrack !== 'function') return;
    const panel = a.closest('[data-vita-mega-panel]');
    const column = a.closest('[data-vita-nav-column]');
    const links = [...(column || panel).querySelectorAll('a[href]')];
    window.vitaTrack('nav_click', {
      menu: panel.dataset.menu || '',
      column: column?.dataset.vitaNavColumn || '',
      label: a.textContent.replace(/\s+/g, ' ').replace('→', '').trim().slice(0, 100),
      position: links.indexOf(a) + 1,
      url: a.getAttribute('href'),
    });
  };
}

if (!customElements.get('vita-mega-nav')) customElements.define('vita-mega-nav', VitaMegaNav);
